import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { CustomerUser } from './auth.service';

/** A wallet / reward-points ledger entry (from the `transactions` collection). */
export interface LedgerTxn {
  _id?: string;
  kind: 'WALLET' | 'REWARD_POINTS';
  direction: 'Credit' | 'Debit';
  amount: number;
  balanceAfter?: number;
  reason?: string;
  order_id?: string;
  createdAt?: string;
}

/** An address as stored/returned by the My Addresses API (`addresses` collection). */
export interface ApiAddress {
  _id?: string;
  name: string;
  phone: string;
  flat?: string;
  street?: string;
  landmark?: string;
  pincode: string;
  city: string;
  state: string;
  country: string;
  saveAs?: 'Home' | 'Work' | 'Other';
  isDefault?: boolean;
}

/** A gift voucher owned/received by the customer (`gift_vouchers` collection). */
export interface GiftVoucher {
  _id?: string;
  code: string;
  initialAmount: number;
  balance: number;
  recipientEmail?: string;
  expiresAt?: string;
  isRedeemed?: boolean;
  status?: string;
  createdAt?: string;
}

/**
 * Authenticated customer data — profile (/account) and address book (/addresses).
 * The customer's tokens are attached by customerAuthInterceptor; ownership is enforced
 * server-side from the identity token, so these calls only ever touch the caller's own data.
 */
@Injectable({ providedIn: 'root' })
export class AccountService {
  private http = inject(HttpClient);
  private accountUrl = `${environment.customApiBase}/account`;
  private addressesUrl = `${environment.customApiBase}/addresses`;
  private giftVouchersUrl = `${environment.customApiBase}/gift-vouchers`;

  getProfile(): Observable<CustomerUser> {
    return this.http.post<unknown>(this.accountUrl, { op: 'get' }).pipe(map((r) => unwrap<CustomerUser>(r)));
  }

  /** Wallet / reward-points ledger for the signed-in customer (optionally filtered by kind). */
  getTransactions(kind?: 'WALLET' | 'REWARD_POINTS'): Observable<LedgerTxn[]> {
    return this.http
      .post<unknown>(`${environment.customApiBase}/transactions`, kind ? { kind } : {})
      .pipe(map((r) => unwrap<{ records: LedgerTxn[] }>(r).records ?? []));
  }

  updateProfile(data: Partial<CustomerUser>): Observable<CustomerUser> {
    return this.http.post<unknown>(this.accountUrl, { op: 'update', data }).pipe(map((r) => unwrap<CustomerUser>(r)));
  }

  listAddresses(): Observable<ApiAddress[]> {
    return this.http
      .post<unknown>(this.addressesUrl, { op: 'list' })
      .pipe(map((r) => unwrap<{ records: ApiAddress[] }>(r).records ?? []));
  }

  createAddress(data: ApiAddress): Observable<ApiAddress> {
    return this.http.post<unknown>(this.addressesUrl, { op: 'create', data }).pipe(map((r) => unwrap<ApiAddress>(r)));
  }

  updateAddress(id: string, data: Partial<ApiAddress>): Observable<ApiAddress> {
    return this.http.post<unknown>(this.addressesUrl, { op: 'update', id, data }).pipe(map((r) => unwrap<ApiAddress>(r)));
  }

  deleteAddress(id: string): Observable<unknown> {
    return this.http.post<unknown>(this.addressesUrl, { op: 'delete', id }).pipe(map((r) => unwrap(r)));
  }

  setDefaultAddress(id: string): Observable<unknown> {
    return this.http.post<unknown>(this.addressesUrl, { op: 'setDefault', id }).pipe(map((r) => unwrap(r)));
  }

  changePassword(oldPassword: string, newPassword: string): Observable<unknown> {
    return this.http.post<unknown>(this.accountUrl, { op: 'changePassword', oldPassword, newPassword }).pipe(map((r) => unwrap(r)));
  }

  /** Soft-deletes the caller's account (status → Inactive). */
  deleteAccount(): Observable<unknown> {
    return this.http.post<unknown>(this.accountUrl, { op: 'delete' }).pipe(map((r) => unwrap(r)));
  }

  /** Gift vouchers the customer purchased or received. */
  getGiftVouchers(): Observable<GiftVoucher[]> {
    return this.http.post<unknown>(this.giftVouchersUrl, { op: 'list' }).pipe(map((r) => unwrap<{ records: GiftVoucher[] }>(r).records ?? []));
  }
}

/** Unwrap the standard API Maker envelope `{ success, data, errors }`. */
function unwrap<T>(res: unknown): T {
  if (res && typeof res === 'object') {
    const r = res as { success?: boolean; data?: unknown; errors?: { message?: string }[] };
    if (r.success === false) {
      throw new Error(r.errors?.map((e) => e.message).filter(Boolean).join('; ') || 'Request failed.');
    }
    if (r.data !== undefined) return r.data as T;
  }
  return res as T;
}
